import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../App";
import { Button } from "../ui/button";
import {
  LayoutDashboard,
  CalendarDays,
  Clock,
  Trophy,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";

const navItems = [
  { name: "Dashboard", path: "/sdr", icon: LayoutDashboard },
  { name: "Book Appointment", path: "/sdr/calendar", icon: CalendarDays },
  { name: "Hours Log", path: "/sdr/hours", icon: Clock },
  { name: "Leaderboard", path: "/sdr/leaderboard", icon: Trophy },
];

const SDRLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
            {user?.name?.[0] || "S"}
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-white/50 font-mono">{user?.agent_code}</p>
            </div>
          )}
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active ? "bg-sky-500/15 text-sky-400" : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {!collapsed && <span>{item.name}</span>}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        <Button variant="ghost" className="w-full justify-start text-white/50 hover:text-white hover:bg-white/5 gap-3" onClick={logout} data-testid="logout-btn">
          <LogOut className="w-4 h-4" />{!collapsed && "Sign Out"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f0f2f5] flex">
      <aside className={`hidden lg:flex flex-col bg-[#1A2636] fixed top-0 left-0 h-screen z-30 transition-all duration-200 ${collapsed ? "w-[68px]" : "w-[240px]"}`}>
        <Sidebar />
        <button onClick={() => setCollapsed(!collapsed)} className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-[#1A2636] border border-white/10 flex items-center justify-center text-white/50 hover:text-white">
          {collapsed ? <PanelLeft className="w-3 h-3" /> : <PanelLeftClose className="w-3 h-3" />}
        </button>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`fixed top-0 left-0 h-screen w-[260px] bg-[#1A2636] z-50 transform transition-transform lg:hidden ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <Sidebar />
      </aside>

      <div className={`flex-1 transition-all duration-200 ${collapsed ? "lg:ml-[68px]" : "lg:ml-[240px]"}`}>
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-gray-200/60 px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)}><Menu className="w-5 h-5 text-gray-600" /></button>
            <h1 className="text-sm font-semibold text-[#1A2636]">SDR Portal</h1>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="hidden sm:inline">{user?.name}</span>
            <span className="font-mono bg-sky-50 text-sky-700 px-2 py-0.5 rounded">{user?.agent_code}</span>
          </div>
        </header>
        <main className="p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
};

export default SDRLayout;
